/**
 * An access case as a My Tickets row (6 Oct, product owner): "🔑 Access ·
 * Anna Berg — New hire · Waiting for approval · Action for you: Approve".
 *
 * The row opens the person's access page (`/access/<case_id>`), where every
 * button is. Its Ivanti requests (the New User Request, 365FO, CRM, …) are
 * grouped under it in a small expander with their numbers and statuses, and
 * the ticket list leaves them out (`lib/access-rows.ts`).
 */

import { ChevronRight, Icon, KeyRound } from "@norican/icons";
import { Badge, Card, cn, Typography } from "@norican/react";
import { useCallback } from "react";
import { Link } from "react-router";

import { RelativeTime } from "@/components/common";
import { useWaitingWords } from "@/features/access/components/waiting-on";
import { caseStageTone } from "@/features/access/lib/access";
import { useT } from "@/i18n";
import type { AccessCaseRequest, AccessCaseSummary } from "@/lib/api";

import { accessActionKey } from "../lib/access-rows";

/** The "Access" type badge, beside "Something is broken" and "I need something". */
export function AccessKindBadge({ size = "sm" }: { size?: "xs" | "sm" | "md" }) {
  const { t } = useT("tickets");
  return (
    <Badge
      tone="neutral"
      variant="outline"
      size={size}
      shape="rounded"
      className="inline-flex items-center gap-1.5"
    >
      <Icon icon={KeyRound} size="xs" className="text-accent-fg-subtle" />
      {t("kind.ACCESS")}
    </Badge>
  );
}

/** "Anna Berg — New hire". */
export function useAccessRowTitle() {
  const { t } = useT("tickets");
  return useCallback(
    (item: Pick<AccessCaseSummary, "person_name" | "type">) =>
      t("accessRow.title", {
        person: item.person_name,
        type: t(`accessRow.type.${item.type}`),
      }),
    [t],
  );
}

/** The case's stage, in the tracker's words and colours. */
export function AccessStageBadge({ item }: { item: Pick<AccessCaseSummary, "stage"> }) {
  const { t } = useT("tickets");
  const stage = item.stage ?? "in_progress";
  return (
    <Badge tone={caseStageTone(stage)} variant="subtle" size="sm" className="whitespace-nowrap">
      {t(`access.stage.${stage}`)}
    </Badge>
  );
}

/** "Action for you: Approve", or nothing when the case asks nothing of the reader. */
export function AccessActionBadge({
  item,
}: {
  item: Pick<AccessCaseSummary, "action_count" | "your_actions">;
}) {
  const { t } = useT("tickets");
  const key = accessActionKey(item);
  if (key === null) {
    return null;
  }
  return (
    // 19 Oct (UI review, finding 3): "action for you" is accent everywhere.
    <Badge tone="accent" variant="solid" size="xs">
      {t(key)}
    </Badge>
  );
}

/** The case's requests, folded under the row: number, what it is, its status. */
export function AccessRequestsExpander({ requests }: { requests: readonly AccessCaseRequest[] }) {
  const { t } = useT("tickets");
  const words = useWaitingWords();
  if (requests.length === 0) {
    return null;
  }
  return (
    <details className="group text-sm">
      <summary className="text-fg-muted hover:text-fg w-fit cursor-pointer text-xs select-none">
        {t("accessRow.requests", { count: requests.length })}
      </summary>
      <ul className="m-0 mt-1 flex list-none flex-col gap-1 p-0">
        {requests.map((request) => (
          <li key={request.ticket_id} className="flex min-w-0 flex-wrap items-center gap-x-2">
            <Link
              to={request.url_path}
              className="text-accent-fg-subtle font-mono text-xs font-semibold underline-offset-2 hover:underline"
            >
              #{request.ticket_number}
            </Link>
            <span className="text-fg min-w-0 text-xs break-words">
              {request.item ? words.shortName(request.item) : request.subject}
            </span>
            {request.provider_status ? (
              <span className={cn("text-xs", request.is_open ? "text-fg-muted" : "text-fg-subtle")}>
                · {request.provider_status}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </details>
  );
}

export interface AccessCaseCardRowProps {
  item: AccessCaseSummary;
  /** Draws the accent edge (the rail's "Waiting on Me"). */
  needsYou?: boolean;
  /**
   * My Tickets on mobile (13 Oct): "Open >" at the end of the card, as its
   * ticket cards have, instead of the rail's bare chevron at the top.
   */
  openLabel?: boolean;
}

/** The case as a card: the rail, and My Tickets below `md`. */
export function AccessCaseCardRow({
  item,
  needsYou = false,
  openLabel = false,
}: AccessCaseCardRowProps) {
  const { t } = useT("tickets");
  const title = useAccessRowTitle();
  return (
    <Card
      padding="sm"
      className={cn(
        "ease-standard flex flex-col gap-2 transition hover:shadow-md",
        needsYou && "border-l-accent border-l-4",
      )}
    >
      <Link to={item.url_path} className="flex flex-col gap-2 rounded-md">
        <span className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            <AccessKindBadge size="xs" />
            <Typography
              as="span"
              variant="caption"
              className="text-accent-fg-subtle font-mono font-semibold tracking-tight"
            >
              {t("access.caseNumber", { number: item.case_number })}
            </Typography>
          </span>
          {openLabel ? null : (
            <Icon icon={ChevronRight} size="sm" className="text-fg-subtle shrink-0" />
          )}
        </span>
        <Typography as="span" variant="body-sm" className="text-fg line-clamp-2 font-medium">
          {title(item)}
        </Typography>
        <span className="flex flex-wrap items-center gap-2">
          <AccessStageBadge item={item} />
          <AccessActionBadge item={item} />
          {item.updated_at ? (
            <Typography
              as="span"
              variant="caption"
              tone="muted"
              className={openLabel ? "ml-auto" : undefined}
            >
              <RelativeTime value={item.updated_at} />
            </Typography>
          ) : null}
          {openLabel ? (
            <span
              aria-hidden
              data-testid="access-card-open"
              className={cn(
                "text-accent-fg-subtle inline-flex items-center gap-0.5 text-sm font-medium",
                !item.updated_at && "ml-auto",
              )}
            >
              {t("list.open")}
              <Icon icon={ChevronRight} size="sm" />
            </span>
          ) : null}
        </span>
      </Link>
      <AccessRequestsExpander requests={item.requests ?? []} />
    </Card>
  );
}
